import React, { useEffect } from 'react'
import useTooltipStore from '@/data/store/useTooltipStore';
import { Text, Pressable, Modal, View, Platform } from 'react-native';
import usePlatform from '@/utils/hooks/usePlatform';
import { impactLight } from '@/utils/haptic';
import { StyleSheet } from 'react-native-unistyles';

// Conditionally import createPortal only on web
const createPortal = Platform.OS === 'web' ? require('react-dom').createPortal : null;

// Detect touch-only web devices (no hover) once. On such devices the tooltip
// overlay must capture taps to dismiss; on desktop the overlay stays
// pointerEvents:none so the trigger's hover-out closes it (an auto overlay
// would intercept mouse events and cause open/close flicker).
const isWebTouchOnly = (() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia('(hover: none) and (pointer: coarse)').matches;
})();

// When inside a native modal (ModalWrapper), touch event pageY is relative to the
// modal's content area (below safe area), but the tooltip's Modal starts at screen
// y=0. This offset (safe area top inset) is added to Y to correct the mismatch.
// Default 0 = no offset (outside modals, pageY is already screen-absolute).
export const TooltipOffsetContext = React.createContext(0);

function TooltipContainer({modal}) {
    // This is the single root-mounted renderer, so it legitimately needs all
    // the position/content fields — select them individually rather than the
    // whole store object.
    const isOpen = useTooltipStore(s => s.isOpen);
    const close = useTooltipStore(s => s.close);
    const text = useTooltipStore(s => s.text);
    const subtitle = useTooltipStore(s => s.subtitle);
    const left = useTooltipStore(s => s.left);
    const top = useTooltipStore(s => s.top);
    const width = useTooltipStore(s => s.width);
    const { isWeb } = usePlatform();

    if(isWeb && modal) return <></>

    // Native: render the shared tooltip box from this root-mounted container,
    // wrapped in its own Modal so it presents above any open user modals.
    // (Used to be a per-Tooltip Modal inside each component — but non-Tooltip
    // openers like InlineHelpIcon can't host a Modal inside a <Text>, so they
    // opened the store with nothing rendering it.)
    if(!isWeb) {
        if (!isOpen) return null;
        return (
            <Modal
                visible={true}
                transparent={true}
                animationType="none"
                statusBarTranslucent={true}
                onRequestClose={close}
            >
                <Pressable style={styles.modalOverlay} onPress={close}>
                    <View style={styles.tooltipBox({top, left, width}, false)} pointerEvents="none">
                        <Text style={styles.text}>{text?.split('\n').map((line, i) => (
                            <React.Fragment key={i}>
                                {i > 0 && '\n'}
                                {line}
                            </React.Fragment>
                        ))}</Text>
                        {!!subtitle && <Text style={styles.subtitle}>{subtitle}</Text>}
                    </View>
                </Pressable>
            </Modal>
        );
    }

    // For web, use a portal to render directly to document.body
    if (isWeb && createPortal) {
        if (!isOpen) return null;

        const tooltipContent = (
            <Pressable
                style={[styles.webPortalOverlay, isWebTouchOnly && styles.webPortalOverlayTouch]}
                onPress={close}
            >
                <View style={styles.tooltipBox({top, left, width}, modal)}>
                    <Text style={styles.text}>{text?.split('\n').map((line, i) => (
                        <React.Fragment key={i}>
                            {i > 0 && '\n'}
                            {line}
                        </React.Fragment>
                    ))}</Text>
                    {!!subtitle && <Text style={styles.subtitle}>{subtitle}</Text>}
                </View>
            </Pressable>
        );

        return createPortal(tooltipContent, document.body);
    }

    return null;
}


const Tooltip = React.memo(({children, title, subtitle, onPress = null, style = {}, appearOnTouch = false, testID = null, hitSlop = null, accessibilityLabel}) => {
    // Subscribe only to the stable open/close actions so each mounted Tooltip
    // never re-renders on store writes; read transient state via getState()
    // inside event handlers instead.
    const open = useTooltipStore(s => s.open);
    const close = useTooltipStore(s => s.close);
    const { isNative } = usePlatform();
    const id = React.useId();
    const modalYOffset = React.useContext(TooltipOffsetContext);

    const showTooltip = (e) => {
        if(!useTooltipStore.getState().isOpen && title){
            const timeToClose = Math.max(title.split(" ").length * 250, subtitle ? subtitle.split(" ").length * 250 : 0, 1500);

            if (isNative) impactLight();

            // pageX/pageY are relative to the RN root view. Inside native modals
            // (ModalWrapper), the root starts below the safe area, so pageY is
            // smaller than the actual screen Y by insets.top. modalYOffset
            // (from TooltipOffsetContext, provided by ModalWrapper) corrects this.
            // Outside modals, modalYOffset is 0 and pageY is already screen-absolute.
            const { pageX, pageY } = e.nativeEvent;
            open(pageX, pageY + modalYOffset, title, subtitle, id);

            setTimeout(() => {
                close();
            }, timeToClose);
        }
    }

    useEffect(() => {
        return () => {
            close();
        }
    }, [])

    return (<>
        <Pressable
            style={style}
            accessibilityRole={appearOnTouch ? 'button' : undefined}
            accessibilityLabel={accessibilityLabel}
            onPress={(e) => {
                if(appearOnTouch){
                   showTooltip(e);
                }
                else{
                    if(typeof onPress == "function") onPress(e);
                    close();
                }
            }}
            onMouseLeave={() => {
                if (isWebTouchOnly) return;
                close();
            }}
            onHoverIn={(e) => {
                // Touch-only web fires synthesized hover on tap — skip so the
                // press (appearOnTouch) path with its auto-close timeout runs.
                if (isWebTouchOnly || useTooltipStore.getState().isOpen || !title) return
                open(e.nativeEvent.pageX, e.nativeEvent.pageY, title, subtitle, id)
            }}
            onHoverOut={() => {
                if (isWebTouchOnly) return;
                close();
            }}
            delayLongPress={200}
            onLongPress={showTooltip}
            testID={testID}
            hitSlop={hitSlop}
        >
            {children}
        </Pressable>
    </>)
})


const styles = StyleSheet.create(({colors, fonts}) => ({
    webPortalOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 999999,
        pointerEvents: 'none'
    },
    // Touch-only web: let taps on the overlay dismiss the tooltip.
    webPortalOverlayTouch: {
        pointerEvents: 'auto',
    },
    modalOverlay: {
        flex: 1,
        backgroundColor: 'transparent',
    },
    tooltipBox: (tooltip, modal) => ({
        position: 'absolute',
        top: modal ? tooltip.top + 80 : tooltip.top,
        left: modal ? tooltip.left - 10 : tooltip.left,
        width: tooltip.width || undefined,
        maxWidth: tooltip.width || undefined,
        alignItems: 'center',
        justifyContent: 'center',
        paddingVertical: 5,
        paddingHorizontal: 10,
        backgroundColor: colors.surface,
        borderRadius: 5,
        boxShadow: '0px 1px 5px 1px rgba(0, 0, 0, 0.1)',
        borderWidth: 1,
        borderColor: colors.border,
    }),
    text: {
        color: colors.text,
        fontSize: 12,
        textAlign: 'center',
        fontFamily: fonts.heading
    },
    subtitle: {
        color: colors.text,
        fontSize: 11,
        textAlign: 'center',
        fontFamily: fonts.body,
        marginTop: 2,
        fontStyle: 'italic'
    }
}))

export default React.memo(TooltipContainer);
export { Tooltip };